import React, { forwardRef, useState } from 'react';
import type {
  FieldErrors,
  FieldValues,
  RegisterOptions,
  UseFormRegister,
} from 'react-hook-form';

import getRegExp from '@/utils/getRegExp';

import { IconEye, IconEyeOff } from '@/components/apps/ui/icon';

import ButtonIcon from '../../buttons/ButtonIcon';
import DescriptionPassword from '../description/DescriptionPassword';

interface InputProps {
  name: string;
  isHideValid?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  unit?: string;
  watch?: any;
  styleWidthSize?: string;
}

interface InputPasswordWithHookFormProps extends InputProps {
  register?: UseFormRegister<any>;
  errors?: FieldErrors<FieldValues>;
  rules?: RegisterOptions;
}

const passwordPattern = {
  value: getRegExp('password_en_num_special'),
};

export default function withPasswordHookForm<
  T extends React.ComponentType<any>,
>(Component: T) {
  const WrappedComponent = forwardRef(
    (
      {
        name,
        isHideValid = false,
        register,
        errors,
        rules,
        onChange,
        onBlur,
        disabled,
        readOnly,
        watch,
        styleWidthSize = 'auto',
        ...props
      }: InputProps & {
        register?: any;
        errors?: any;
        rules?: any;
        watch?: any;
      },
      ref: React.ForwardedRef<any>,
    ) => {
      const hasError = !!errors?.[name];
      const showDescription = !isHideValid;
      const passwordRules = {
        required: true,
        ...(!isHideValid && {
          minLength: {
            value: 8,
          },
          pattern: passwordPattern,
        }),
        ...rules,
      };

      const [inputShow, setInputShow] = useState(false); // 비밀번호 숨김 / 표기

      const inputShowHandler = () => {
        setInputShow(!inputShow);
      };

      return (
        <div>
          <Component
            type={inputShow ? 'text' : 'password'}
            hasError={hasError}
            disabled={disabled}
            readOnly={readOnly}
            ref={ref}
            {...(register &&
              register(name, {
                onChange: onChange,
                onBlur: onBlur,
                ...passwordRules,
              }))}
            rightAction={
              <ButtonIcon
              className="-mb-1 -mr-3 -mt-1 ml-0 flex items-center disabled:[opacity:revert]"
                onClick={inputShowHandler}
                disabled={disabled}
                type="button"
              >
                {inputShow ? (
                  <IconEye size={16} opacity={0.5} />
                ) : (
                  <IconEyeOff
                    size={16}
                    opacity={0.5}
                    style={{ position: 'relative', top: '1px' }}
                  />
                )}
              </ButtonIcon>
            }
            styleWidthSize={styleWidthSize}
            {...props}
          />
          {showDescription && (
            <DescriptionPassword
              name={name}
              watch={watch}
              styleWidthSize={styleWidthSize}
            />
          )}
        </div>
      );
    },
  ) as React.ComponentType<
    React.ComponentPropsWithoutRef<T> &
      Omit<InputPasswordWithHookFormProps, 'ref'>
  >;

  return WrappedComponent;
}

